// Frontend permissions mirror — keep in sync with backend security.py
// Each menu item declares the roles allowed to see it.

export const ROLE_LABELS = {
    super_admin: "Super Admin",
    owner: "Owner",
    hr_admin: "HR Admin",
    hr_manager: "HR Manager",
    hr_staff: "HR Staff",
    payroll_officer: "Payroll Officer",
    accounting_manager: "Accounting Manager",
    general_manager: "General Manager",
    manager: "Manager",
    employee: "Employee",
};

export function effectiveRoles(user, activeCompanyId) {
    if (!user) return new Set();
    const roles = user.roles || [];
    const set = new Set();
    for (const r of roles) {
        if (r.company_id === null || r.company_id === undefined || r.company_id === activeCompanyId) {
            set.add(r.role);
        }
    }
    return set;
}

export function hasAnyRole(user, activeCompanyId, allowed) {
    const eff = effectiveRoles(user, activeCompanyId);
    if (eff.has("super_admin")) return true;
    for (const r of allowed) if (eff.has(r)) return true;
    return false;
}

export function isPureEmployee(user, activeCompanyId) {
    const eff = effectiveRoles(user, activeCompanyId);
    return eff.has("employee") && eff.size === 1;
}

export function isPureManager(user, activeCompanyId) {
    const eff = effectiveRoles(user, activeCompanyId);
    if (eff.has("super_admin")) return false;
    return eff.has("manager") && !["hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"].some((r) => eff.has(r));
}

// Highest-priority home path for the user
export function roleHomePath(user, activeCompanyId) {
    const eff = effectiveRoles(user, activeCompanyId);
    if (eff.has("owner")) return "/owner";
    if (eff.has("super_admin") || eff.has("hr_admin") || eff.has("general_manager") || eff.has("accounting_manager")) return "/dashboard";
    if (eff.has("hr_manager")) return "/payroll/settings";
    if (eff.has("payroll_officer")) return "/payroll/runs";
    if (eff.has("hr_staff")) return "/dashboard";
    if (eff.has("manager")) return "/team";
    return "/me";
}

// Permission map per nav item key
export const NAV_ALLOW = {
    reports_center: ["super_admin", "hr_admin", "payroll_officer", "accounting_manager", "general_manager"],
    payroll_loans: ["super_admin", "hr_admin", "payroll_officer", "accounting_manager", "general_manager"],
    user_manual: ["super_admin", "hr_admin", "hr_manager", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"],
    dashboard: ["super_admin", "hr_admin", "hr_staff", "general_manager", "accounting_manager", "payroll_officer"],
    people_analytics: ["hr_manager"],
    owner_overview: ["super_admin", "owner", "general_manager"],
    admin_rbac: ["super_admin", "hr_admin"],
    employees: ["super_admin", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager", "manager"],
    organization: ["super_admin", "hr_admin", "hr_staff", "general_manager", "accounting_manager", "manager"],
    clock_in: ["super_admin", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager", "manager", "employee"],
    attendance: ["super_admin", "hr_admin", "hr_staff", "payroll_officer", "manager"],
    shifts: ["super_admin", "hr_admin", "hr_staff", "general_manager", "payroll_officer", "manager"],
    holidays_admin: ["super_admin", "hr_admin", "hr_staff", "payroll_officer", "general_manager"],
    document_requests: ["super_admin", "hr_admin", "hr_staff", "payroll_officer"],
    contracts: ["super_admin", "hr_admin", "hr_staff", "hr_manager", "payroll_officer", "general_manager", "owner"],
    employee_import: ["super_admin", "hr_admin", "hr_manager", "hr_staff"],
    data_migration: ["super_admin", "hr_admin", "hr_manager", "hr_staff"],
    biometric_devices: ["super_admin", "hr_admin", "hr_manager", "hr_staff"],
    sso: ["super_admin", "hr_admin"],
    api_access: ["super_admin", "hr_admin"],
    my_requests: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "hr_manager", "payroll_officer", "accounting_manager", "general_manager"],
    adjustments: ["super_admin", "hr_admin", "hr_staff", "hr_manager", "manager", "general_manager"],
    offboarding: ["super_admin", "hr_admin", "hr_staff", "hr_manager", "general_manager", "accounting_manager", "payroll_officer"],
    bonuses: ["hr_manager", "accounting_manager", "hr_admin", "payroll_officer", "general_manager"],
    notifications: ["super_admin", "owner", "employee", "manager", "hr_admin", "hr_staff", "hr_manager", "payroll_officer", "accounting_manager", "general_manager"],
    leaves: ["super_admin", "hr_admin", "hr_staff", "manager"],
    overtime: ["super_admin", "hr_admin", "hr_staff", "manager"],
    payroll_runs: ["super_admin", "payroll_officer", "accounting_manager", "general_manager", "hr_admin"],
    payroll_control: ["super_admin", "payroll_officer", "accounting_manager", "general_manager", "hr_admin"],
    final_pay: ["super_admin", "payroll_officer", "accounting_manager", "general_manager", "hr_admin"],
    compliance_readiness: ["super_admin", "payroll_officer", "accounting_manager", "general_manager", "hr_admin"],
    compliance_reports: ["super_admin", "payroll_officer", "accounting_manager", "general_manager", "hr_admin"],
    contribution_tables: ["super_admin", "payroll_officer", "accounting_manager", "general_manager", "hr_admin"],
    payroll_settings: ["super_admin", "hr_manager", "accounting_manager", "hr_admin", "general_manager", "payroll_officer"],
    disbursement: ["super_admin", "payroll_officer", "accounting_manager", "general_manager", "hr_admin"],
    learning: ["super_admin", "hr_admin", "hr_staff", "general_manager", "manager"],
    my_learning: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "hr_manager", "payroll_officer", "accounting_manager", "general_manager"],
    engagement: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "hr_manager", "payroll_officer", "accounting_manager", "general_manager"],
    rewards: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "hr_manager", "payroll_officer", "accounting_manager", "general_manager"],
    recruitment: ["super_admin", "hr_admin", "hr_staff"],
    onboarding: ["super_admin", "hr_admin", "hr_staff"],
    performance: ["super_admin", "hr_admin", "hr_staff", "manager"],
    training: ["super_admin", "hr_admin", "hr_staff"],
    relations: ["super_admin", "hr_admin", "hr_staff"],
    helpdesk: ["super_admin", "hr_admin", "hr_staff"],
    benefits: ["super_admin", "hr_admin", "hr_staff"],
    assets: ["super_admin", "hr_admin", "hr_staff"],
    admin_users: ["super_admin", "hr_admin"],
    admin_companies: ["super_admin", "hr_admin"],
    admin_audit: ["super_admin", "hr_admin", "accounting_manager", "general_manager"],
    admin_automation: ["super_admin", "hr_admin", "accounting_manager", "general_manager"],
    admin_minimum_wage: ["super_admin", "hr_admin", "accounting_manager", "general_manager"],
    admin_leave_policies: ["super_admin", "hr_admin", "general_manager"],
    ai_assistant: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"],
    ai_admin: ["super_admin", "hr_admin", "hr_staff", "general_manager"],
    approvals_inbox: ["super_admin", "hr_admin", "hr_staff", "manager", "general_manager", "accounting_manager", "payroll_officer"],
    announcements: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"],
    // Self-service & team
    my_dashboard: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"],
    my_profile: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"],
    my_time: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"],
    my_payslips: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"],
    my_tickets: ["super_admin", "employee", "manager", "hr_admin", "hr_staff", "payroll_officer", "accounting_manager", "general_manager"],
    team: ["manager", "general_manager"],
    team_leaves: ["super_admin", "manager", "hr_admin", "hr_staff"],
    team_overtime: ["super_admin", "manager", "hr_admin", "hr_staff"],
    team_attendance: ["super_admin", "manager", "hr_admin", "hr_staff"],
};

export function canSee(user, activeCompanyId, key) {
    const eff = effectiveRoles(user, activeCompanyId);
    if (eff.has("super_admin")) return true;
    // Dynamic RBAC map from /auth/me ({role: [module keys]}) wins when present;
    // roles missing from the map fall back to the static defaults below.
    const rbac = user?.__rbac;
    if (rbac && Object.keys(rbac).length) {
        for (const r of eff) {
            const mods = rbac[r];
            if (mods ? mods.includes(key) : (NAV_ALLOW[key] || []).includes(r)) return true;
        }
        return false;
    }
    return hasAnyRole(user, activeCompanyId, NAV_ALLOW[key] || []);
}

// Toggleable operational modules (mirror backend admin.py MODULE_CATALOG).
// Core areas (dashboard, employees, organization, admin, self-service, team)
// are always available and not listed here.
export const MODULE_CATALOG = [
    { key: "timekeeping", label: "Timekeeping & Attendance" },
    { key: "leaves", label: "Leave Management" },
    { key: "overtime", label: "Overtime" },
    { key: "payroll", label: "Payroll" },
    { key: "compliance", label: "Government Compliance" },
    { key: "recruitment", label: "Recruitment" },
    { key: "onboarding", label: "Onboarding" },
    { key: "performance", label: "Performance" },
    { key: "training", label: "Training" },
    { key: "relations", label: "Employee Relations" },
    { key: "helpdesk", label: "Helpdesk" },
    { key: "benefits", label: "Benefits" },
    { key: "assets", label: "Assets" },
    { key: "announcements", label: "Announcements" },
    { key: "ai_hr_agent", label: "AI HR Agent (Premium)", premium: true },
];

// Map each nav item key to the module that governs its visibility.
// Keys not present here are core/always-on (still role-gated).
export const NAV_KEY_TO_MODULE = {
    attendance: "timekeeping", schedules: "timekeeping", clock_in: "timekeeping", my_time: "timekeeping", team_attendance: "timekeeping",
    leaves: "leaves", team_leaves: "leaves",
    overtime: "overtime", team_overtime: "overtime",
    payroll_runs: "payroll", payroll_control: "payroll", final_pay: "payroll", my_payslips: "payroll",
    compliance_readiness: "compliance", compliance_reports: "compliance", contribution_tables: "compliance",
    recruitment: "recruitment",
    onboarding: "onboarding",
    performance: "performance",
    training: "training",
    relations: "relations",
    helpdesk: "helpdesk", my_tickets: "helpdesk",
    benefits: "benefits",
    assets: "assets",
    announcements: "announcements",
    ai_assistant: "ai_hr_agent", ai_admin: "ai_hr_agent",
};

// Whether a nav item is enabled for the active company (module not hidden).
export function moduleEnabledForNav(navKey, company) {
    const mod = NAV_KEY_TO_MODULE[navKey];
    if (!mod) return true; // core item
    const disabled = (company && company.disabled_modules) || [];
    return !disabled.includes(mod);
}
